/**
 * Stolperstein List Renderer
 * Renders the list of Stolpersteine with icons for related content
 */

import type { StolpersteineData, MedienprojekteData, FilmproduktionenData } from '../../types';

interface MedienprojektLookup {
  [key: number]: { slug: string; titel: string };
}

interface FilmproduktionLookup {
  [key: number]: { slug: string; titel: string };
}

export function renderStolpersteinList(
  data: StolpersteineData | null,
  medienprojekteData: MedienprojekteData | null,
  filmproduktionenData: FilmproduktionenData | null
): void {
  const container = document.getElementById('stolperstein-list');
  if (!container || !data) return;

  // Create lookup maps for slugs and titles (key = id)
  const medienprojektData: MedienprojektLookup = {};
  if (medienprojekteData?.projekte) {
    medienprojekteData.projekte.forEach(p => {
      medienprojektData[p.id] = { slug: p.slug, titel: p.titel };
    });
  }

  const filmproduktionData: FilmproduktionLookup = {};
  if (filmproduktionenData?.produktionen) {
    filmproduktionenData.produktionen.forEach(f => {
      filmproduktionData[f.id] = { slug: f.slug, titel: f.titel };
    });
  }

  const html = data.stolpersteine.map(stone => {
    const medienprojektIds = stone.medienprojekt_ids || [];
    const filmproduktionIds = stone.filmproduktion_ids || [];

    // Generate icons for all medienprojekte
    const medienprojektIcons = medienprojektIds.map(id => {
      const projekt = medienprojektData[id];
      if (!projekt) return '';
      return `
          <a href="medienprojekt/${projekt.slug}.html" class="map-list__icon-link" title="Zum Medienprojekt: ${projekt.titel}">
            <span class="map-list__icon">
              <img src="./img/icons/icon-project-pupil-orange.svg" alt="Medienprojekt">
            </span>
          </a>`;
    }).join('');

    // Generate icons for all filmproduktionen
    const filmproduktionIcons = filmproduktionIds.map(id => {
      const filmproduktion = filmproduktionData[id];
      if (!filmproduktion) return '';
      return `
          <a href="filmproduktion/${filmproduktion.slug}.html" class="map-list__icon-link" title="Zur Projektdokumentation: ${filmproduktion.titel}">
            <span class="map-list__icon">
              <img src="./img/icons/icon-project-film.svg" alt="Filmproduktion">
            </span>
          </a>`;
    }).join('');

    return `
    <li class="map-list__item" data-id="${stone.id}">
      <span class="map-list__number">${stone.nummer}</span>
      <a href="stolperstein/${stone.slug}.html" class="map-list__name" title="Zum Stolperstein von ${stone.name}">${stone.name}</a>
      <div class="map-list__icons">
        ${medienprojektIcons}
        ${filmproduktionIcons}
      </div>
    </li>
  `}).join('');

  container.innerHTML = html;

  // Interaktion: ganzes Item oeffnet die Detailseite; Hover hebt den passenden Kartenmarker hervor.
  const canHover = window.matchMedia('(hover: hover)').matches;

  container.querySelectorAll<HTMLElement>('.map-list__item').forEach(item => {
    const nameLink = item.querySelector<HTMLAnchorElement>('.map-list__name');
    const href = nameLink?.getAttribute('href') || null;
    const id = item.dataset.id;

    // Klick auf das gesamte Item (Zahl oder Text) -> Stolperstein-Detailseite.
    // Icon-Links (Medienprojekt/Film) und der Name-Link navigieren eigenstaendig.
    item.addEventListener('click', (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('.map-list__icon-link') || target.closest('.map-list__name')) return;
      if (href) window.location.href = href;
    });

    // Nur auf Hover-Geraeten: Zeile ueberfahren -> Marker-Zahl auf der Karte hervorheben (ohne Tooltip).
    if (canHover && id) {
      const marker = () => document.querySelector<HTMLElement>(`.map-marker[data-id="${id}"]`);
      item.addEventListener('mouseenter', () => marker()?.classList.add('is-highlight'));
      item.addEventListener('mouseleave', () => marker()?.classList.remove('is-highlight'));
    }
  });
}
